import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../../amplify/data/resource';
import { AppointmentInfoDialogComponent } from '../appointment-info-dialog/appointment-info-dialog.component';
import { AmplifyModelService } from '../../services/amplify-model-service.service';
import {
  CreateServiceRecordDialogComponent,
  CreateServiceRecordDialogData,
} from '../../components/create-service-record-dialog/create-service-record-dialog.component';

const client = generateClient<Schema>();

@Component({
  selector: 'app-appointment-entry',
  imports: [CommonModule],
  templateUrl: './appointment-entry.component.html',
  styleUrl: './appointment-entry.component.css'
})
export class AppointmentEntryComponent {
  @Input() appointment: any;
  @Input() selectedPeriod: any;
  @Input() schedule: any;

  readonly dialog = inject(MatDialog);
  private readonly ams = inject(AmplifyModelService);

  get displayContent(): string {
    if (!this.appointment) return '';
    
    // Handle grouped appointments
    if (this.appointment.isGrouped) {
      return this.getGroupedDisplayContent();
    }
    
    switch (this.selectedPeriod?.id) {
      case 'day':
      case 'workDay':
        // Show full details for day view
        return `${this.appointment.name} \n ${this.getTimeDisplay()}`;
      
      case 'week':
        // Show name and time for week view
        return `${this.appointment.name} ${this.getShortTimeDisplay()}`;
      
      case '2week':
      case 'month':
        // Show just name for month view
        return this.appointment.name;
      
      case 'year':
        // Show minimal info for year view
        return '•';
      
      default:
        return this.appointment.name;
    }
  }

  get tooltipContent(): string {
    if (!this.appointment) return '';
    
    if (this.appointment.isGrouped) {
      const count = this.appointment.count || 0;
      return `${count} appointment${count !== 1 ? 's' : ''}`;
    }
    
    return `${this.appointment.name}${this.appointment.start_date_time ? ' \n ' + this.getTimeDisplay() : ''}`;
  }

  private getGroupedDisplayContent(): string {
    const count = this.appointment.count || 0;
    return `${count}`;
  }

  get entryClasses(): string[] {
    const classes = ['entry'];
    
    if (this.selectedPeriod?.id) {
      classes.push(`entry-${this.selectedPeriod.id}`);
    }
    
    if (this.appointment?.isGrouped) {
      classes.push('entry-grouped');
      
      // Add class based on appointment count for styling
      const count = this.appointment.count || 0;
      if (count > 1) {
        classes.push('entry-multiple');
      }
    }
    
    return classes;
  }

  private getTimeDisplay(): string {
    if (!this.appointment.start_date_time) return '';
    
    const startTime = new Date(this.appointment.start_date_time);
    const endTime = new Date(this.appointment.end_date_time);
    
    return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
  }

  private getShortTimeDisplay(): string {
    if (!this.appointment.start_date_time) return '';
    
    const startTime = new Date(this.appointment.start_date_time);
    return `(${this.formatTime(startTime)})`;
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }

  async onEntryClick(): Promise<void> {
    if (this.appointment?.isGrouped) {
      // Grouped (week/month) entries have no single ScheduleEntry to record
      // a service against - keep the existing drill-down behavior.
      this.openInfoDialog();
      return;
    }

    const technician = await this.getTechnicianIfCurrentUserIsOne();
    if (technician) {
      await this.openCreateServiceRecord(technician);
      return;
    }

    this.openInfoDialog();
  }

  private openInfoDialog(): void {
    const dialogRef = this.dialog.open(AppointmentInfoDialogComponent, {
      data: { 
        appointment: this.appointment,
        selectedPeriod: this.selectedPeriod,
        schedule: this.schedule
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Dialog closed with result:', result);
      }
    });
  }

  /**
   * Resolves the current Cognito user's `User` record when they're in the
   * Technicians (or ADMINS) group - the same "who is the current user"
   * lookup EmployeeScheduleComponent.loadUserAndSchedule() performs - so a
   * technician clicking an appointment goes straight to the service-record
   * flow instead of the generic ScheduleEntry edit dialog. Returns null for
   * any other role, or if the lookup fails, so callers fall back to the
   * existing AppointmentInfoDialogComponent behavior.
   */
  private async getTechnicianIfCurrentUserIsOne(): Promise<any | null> {
    try {
      const groups = await this.ams.getUserGroups();
      if (!groups.includes('Technicians') && !groups.includes('ADMINS')) return null;

      const cognitoUser = await this.ams.getCurrentUser();
      if (!cognitoUser?.username) return null;

      const usersResult = await client.models['User']['listByCognitoUsername']({
        cognito_username: cognitoUser.username,
      });
      return usersResult.data?.[0] ?? null;
    } catch (error) {
      console.error('Error resolving current technician:', error);
      return null;
    }
  }

  /**
   * Resolves the appointment's full ScheduleEntry record (including the
   * ServiceAddress relation, which the Scheduler's own observeQuery doesn't
   * include - see SchedulerService.getScheduleEntriesInRange()) and opens
   * CreateServiceRecordDialogComponent for it, matching
   * EmployeeScheduleComponent.openCreateServiceRecord()'s resolve pattern.
   * Falls back to the generic info dialog if the ScheduleEntry can't be
   * resolved.
   */
  private async openCreateServiceRecord(technician: any): Promise<void> {
    const scheduleEntry = await this.resolveScheduleEntry(this.appointment.id);
    if (!scheduleEntry) {
      this.openInfoDialog();
      return;
    }

    // If a CompletedService already exists for this appointment, pass its
    // id through so the dialog opens in edit mode against that existing
    // record instead of creating a duplicate one - mirrors
    // EmployeeScheduleComponent.openCreateServiceRecord()'s same check.
    const existingCompletedService = await this.resolveExistingCompletedService(this.appointment.id);

    this.dialog.open(CreateServiceRecordDialogComponent, {
      data: {
        appointment: scheduleEntry,
        technician,
        existingCompletedServiceId: existingCompletedService?.id,
      } as CreateServiceRecordDialogData,
    });
  }

  /**
   * Looks up whether a CompletedService record already exists for this
   * appointment. Returns null (rather than throwing) on any lookup
   * failure, so this falls back to create mode rather than blocking the
   * dialog entirely.
   */
  private async resolveExistingCompletedService(appointmentId: string): Promise<{ id: string } | null> {
    try {
      const { data } = await client.models['CompletedService']['list']({
        filter: { appointmentId: { eq: appointmentId } },
        selectionSet: ['id'] as any,
      });
      const existing = data?.[0] as any;
      return existing?.id ? { id: existing.id } : null;
    } catch (error) {
      console.error('Error resolving existing CompletedService:', error);
      return null;
    }
  }

  private async resolveScheduleEntry(appointmentId: string): Promise<any | null> {
    try {
      const { data } = await client.models['ScheduleEntry']['get'](
        { id: appointmentId },
        {
          selectionSet: [
            'id', 'name', 'appointment_type', 'start_date_time', 'end_date_time',
            'subId', 'serviceAddressId',
            'serviceAddress.id', 'serviceAddress.property_owner_name',
            'serviceAddress.address.street_address', 'serviceAddress.address.unit_number',
            'serviceAddress.address.city', 'serviceAddress.address.state', 'serviceAddress.address.zip_code',
            // Feeds TreatmentBuilderComponent's Treatment_Start/Treatment_End
            // defaulting (see CreateServiceRecordDialogComponent.openTreatmentBuilder()).
            'serviceTypeId', 'serviceType.id', 'serviceType.default_time_minutes',
          ] as any,
        }
      );
      return data ?? null;
    } catch (error) {
      console.error('Error resolving ScheduleEntry:', error);
      return null;
    }
  }
}
